
import { format, addMinutes } from "date-fns";
import moment from "moment-timezone";

/**
 * Checks if a Date object is valid
 */
const isValidDate = (date: Date | null | undefined): date is Date => {
    return date != null && !isNaN(date.getTime());
};

/**
 * Converts an Excel serial number to a JavaScript Date
 * Excel dates are stored as days since 1900-01-01 (with 1900-02-29 incorrectly included)
 */
export const excelSerialToDate = (serial: number | string): Date | null => {
    const serialNumber = typeof serial === 'string' ? parseFloat(serial) : serial;

    if (isNaN(serialNumber)) {
        return null;
    }

    // Excel date epoch starts at 1900-01-01, but incorrectly treats 1900 as a leap year
    // Days before March 1, 1900 need adjustment
    const excelEpoch = new Date(1900, 0, 1);
    const daysOffset = serialNumber > 59 ? serialNumber - 1 : serialNumber; // Adjust for Excel's leap year bug

    const date = new Date(excelEpoch.getTime() + (daysOffset - 1) * 24 * 60 * 60 * 1000);
    return date;
};

/**
 * Checks if a value looks like an Excel serial date number
 */
export const isExcelSerialDate = (value: any): boolean => {
    if (typeof value === 'number') {
        // Excel dates are typically between 1 (1900-01-01) and ~50000 (year ~2036)
        return value > 0 && value < 100000;
    }
    if (typeof value === 'string') {
        const num = parseFloat(value);
        return !isNaN(num) && num > 0 && num < 100000 && value.indexOf('-') === -1 && value.indexOf('/') === -1;
    }
    return false;
};

export const formatDateRangeString = (date: string, date2: string, isFullDay: boolean, is24hrs: boolean) => {
    if (date == undefined) {
        return "";
    }
    if (isFullDay) {
        return format(new Date(date), "yyyy-MM-dd");
    }

    if (is24hrs) {
        return format(new Date(date), "HH:mm") + "-" + format(new Date(date2), "HH:mm");
    }

    if (format(new Date(date), "aaa") == format(new Date(date2), "aaa")) {
        return format(new Date(date), "H:mm") + "-" + format(new Date(date2), "H:mm aaa")
    }
    else {
        return format(new Date(date), "HH:mm aaa") + "-" + format(new Date(date2), "HH:mm aaa")
    }
}

export const formatDateRangeWithDateString = (date: string, date2: string, isFullDay: boolean, is24hrs: boolean) => {
    if (date == undefined) {
        return "";
    }
    if (isFullDay) {
        return format(new Date(date), "yyyy-MM-dd");
    }

    if (is24hrs) {
        return format(new Date(date), "yyyy-MM-dd HH:mm") + "-" + format(new Date(date2), "HH:mm");
    }

    if (format(new Date(date), "aaa") == format(new Date(date2), "aaa")) {
        return format(new Date(date), "yyyy-MM-dd H:mm") + "-" + format(new Date(date2), "H:mm aaa")
    }
    else {
        return format(new Date(date), "yyyy-MM-dd HH:mm aaa") + "-" + format(new Date(date2), "HH:mm aaa")
    }
}

export const getDateStringFromDate = (date: string) => {
    if (!date) {
        return "";
    }
    return format(new Date(date), "yyyy-MM-dd ");
}

export const formatToShortFormat = (date: Date | null | undefined) => {
    if (!isValidDate(date)) {
        return "";
    }

    return format(date, "yyyy-MM-dd");
}

export const formatDateString = (date: string | Date | undefined | null) => {
    return date != undefined ? format(new Date(date), "yyyy-MM-dd HH:mm") : "";
}

export const getTimeStringFromDate = (date: string, is24hrs: boolean) => {
    if (date == undefined) {
        return "";
    }
    return is24hrs ? format(new Date(date), "HH:mm") : format(new Date(date), "HH:mm aaa");
}

export const getTimeRangeString = (date: string, date2: string, is24hrs: boolean) => {
    if (date == undefined) {
        return "";
    }

    if (is24hrs) {
        return format(new Date(date), "HH:mm") + "-" + format(new Date(date2), "HH:mm");
    }

    if (format(new Date(date), "aaa") == format(new Date(date2), "aaa")) {
        return format(new Date(date), "H:mm") + "-" + format(new Date(date2), "H:mm aaa")
    }
    else {
        return format(new Date(date), "HH:mm aaa") + "-" + format(new Date(date2), "HH:mm aaa")
    }
}

export const getUtcDate = (date: Date, timeZone: string) => {
    if (!isValidDate(date)) {
        return new Date();
    }
    var utcDate = moment.utc(moment.tz(moment(date).format("YYYY-MM-DD HH:mm"), timeZone));
    return new Date(utcDate.year(), utcDate.month(), utcDate.date(), utcDate.hours(), utcDate.minutes(), utcDate.seconds(), utcDate.milliseconds())
}

export const getPreviousYear = (subTractNumberOfYears: number) => {
    return moment().subtract(subTractNumberOfYears, 'years').year();
}

export const getCurrentYearAsString = () => {
    return moment().format("YYYY");
}

export const getUpcomingYear = (addNumberOfYears: number) => {
    return moment().add(addNumberOfYears, 'years').year();
}